import React, { forwardRef, useState } from 'react';
import { Typography } from '../Typography';
import PhoneNumberInput from './PhoneNumberInput';
import styles from './TextInput.module.scss';

export interface TextInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  helperText?: string;
  variant?: 'default' | 'outlined' | 'filled';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  required?: boolean;
  multiline?: boolean;
  rows?: number;
  inputType?: 'email' | 'phone' | 'website' | 'card' | 'users' | 'password' | 'default';
  leftElement?: React.ReactNode;
  rightElement?: React.ReactNode;
}

const TextInput = forwardRef<HTMLInputElement | HTMLTextAreaElement, TextInputProps>(
  (
    {
      label,
      error,
      helperText,
      variant = 'default',
      size = 'md',
      fullWidth = false,
      icon,
      iconPosition = 'left',
      required = false,
      multiline = false,
      rows = 3,
      inputType = 'default',
      leftElement,
      rightElement,
      className = '',
      type,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const inputClasses = [
      multiline ? styles.textarea : styles.input,
      error && styles.error,
      styles[`inputType-${inputType}`],
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const containerClasses = [
      multiline ? styles.textareaContainer : styles.inputContainer,
      error && styles.error,
      styles[`inputType-${inputType}`],
    ]
      .filter(Boolean)
      .join(' ');

    const renderLeftElement = () => {
      if (leftElement) return leftElement;

      // Auto-generate icon based on inputType if no custom icon provided
      if (!icon && inputType === 'email') {
        return (
          <div className={styles.iconLeft}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M2 4L8 8L14 4"
                stroke="#85888E"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <rect x="1" y="3" width="14" height="10" rx="2" stroke="#85888E" strokeWidth="1.5" />
            </svg>
          </div>
        );
      }

      if (!icon && inputType === 'password') {
        return (
          <div className={styles.iconLeft}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M4 6V4C4 2.89543 4.89543 2 6 2H10C11.1046 2 12 2.89543 12 4V6"
                stroke="#85888E"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <rect x="2" y="6" width="12" height="8" rx="2" stroke="#85888E" strokeWidth="1.5" />
              <circle cx="8" cy="10" r="1.5" fill="#85888E" />
            </svg>
          </div>
        );
      }

      if (icon && iconPosition === 'left') {
        return <div className={styles.iconLeft}>{icon}</div>;
      }
      return null;
    };

    const renderRightElement = () => {
      if (rightElement) return rightElement;

      // Password toggle button
      if (inputType === 'password') {
        return (
          <button
            type="button"
            className={styles.passwordToggle}
            onClick={() => setShowPassword(!showPassword)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M8 3C4.5 3 1.73 5.11 1 8C1.73 10.89 4.5 13 8 13C11.5 13 14.27 10.89 15 8C14.27 5.11 11.5 3 8 3Z"
                  stroke="#85888E"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle cx="8" cy="8" r="2" stroke="#85888E" strokeWidth="1.5" />
                <path d="M1 1L15 15" stroke="#85888E" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M8 3C4.5 3 1.73 5.11 1 8C1.73 10.89 4.5 13 8 13C11.5 13 14.27 10.89 15 8C14.27 5.11 11.5 3 8 3Z"
                  stroke="#85888E"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle cx="8" cy="8" r="2" stroke="#85888E" strokeWidth="1.5" />
              </svg>
            )}
          </button>
        );
      }

      // Auto-generate help icon if no custom icon provided
      if (!icon && iconPosition === 'right') {
        return (
          <div className={styles.iconRight}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="7" stroke="#85888E" strokeWidth="1" />
              <path d="M8 5.5V8L9.5 9.5" stroke="#85888E" strokeWidth="1" strokeLinecap="round" />
            </svg>
          </div>
        );
      }

      if (icon && iconPosition === 'right') {
        return <div className={styles.iconRight}>{icon}</div>;
      }
      return null;
    };

    // If inputType is phone, render PhoneNumberInput
    if (inputType === 'phone') {
      // Filter props to ensure defaultValue is string only
      const { defaultValue, ...restProps } = props as any;
      const phoneProps = {
        ...restProps,
        ...(typeof defaultValue === 'string' ? { defaultValue } : {}),
      };

      return (
        <PhoneNumberInput
          ref={ref}
          label={label}
          error={error}
          helperText={helperText}
          variant={variant}
          size={size}
          fullWidth={fullWidth}
          required={required}
          className={className}
          {...phoneProps}
        />
      );
    }

    return (
      <div className={`${styles.inputField} ${fullWidth ? styles.fullWidth : ''}`}>
        {label && (
          <Typography
            variant="text-sm"
            as="label"
            color={error ? 'error' : 'secondary'}
            weight="normal"
            className={styles.label}
          >
            {label}
            {required && <span className={styles.required}>*</span>}
          </Typography>
        )}

        <div className={containerClasses}>
          {renderLeftElement()}

          {multiline ? (
            <textarea
              ref={ref as React.Ref<HTMLTextAreaElement>}
              className={inputClasses}
              rows={rows}
              {...(props as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
            />
          ) : (
            <input
              ref={ref as React.Ref<HTMLInputElement>}
              className={inputClasses}
              type={
                inputType === 'password' ? (showPassword ? 'text' : 'password') : type || inputType
              }
              {...props}
            />
          )}

          {renderRightElement()}
        </div>

        {error && (
          <Typography
            variant="text-sm"
            as="div"
            color="error"
            weight="normal"
            className={styles.errorMessage}
          >
            {error}
          </Typography>
        )}

        {helperText && !error && (
          <Typography
            variant="text-sm"
            as="div"
            color="disabled"
            weight="normal"
            className={styles.helperText}
          >
            {helperText}
          </Typography>
        )}
      </div>
    );
  }
);

TextInput.displayName = 'TextInput';

export default TextInput;
